import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";

// Connects to data-controller="forms--flow-configurations"
export default class extends Controller {
  static targets = [
    "user",
    "timezone",
    "scheduleTimeZone",
    "interval",
    "machines",
    "paceMachines",
    "factory",
    "frequencyContainer",
    "customIntervalContainer",
    "exportDestinationType",
    "destinationApiContainer",
    "destinationGoogleDriveContainer",
    "destinationMicrosoftAzureContainer",
    "destinationUpdateBehaviorContainer",
    "transformationPlanType",
    "destinationUpdateBehavior",
    "microsoftAzureStorageType",
    "microsoftAzureStorageContainer",
    "microsoftAzureFileShareContainer",
  ];

  connect() {
    this.setupSelectFields();

    if (this.hasExportDestinationTypeTarget) {
      this.setExportDestinationTypeVisibility(
        this.exportDestinationTypeSelect.getValue()
      );
    }
  }

  disconnect() {
    this.userSelect?.destroy();
    this.timezoneSelect?.destroy();
    this.machinesSelect?.destroy();
    this.paceMachinesSelect?.destroy();
    this.factorySelect?.destroy();
    this.intervalSelect?.destroy();
    this.destinationUpdateBehaviorSelect?.destroy();
    this.microsoftAzureStorageTypeSelect?.destroy();

    if (this.hasExportDestinationTypeTarget) {
      this.exportDestinationTypeSelect.destroy();
    }

    if (this.hasTransformationPlanTypeTarget) {
      this.transformationPlanTypeSelect.destroy();
    }
  }

  setupSelectFields() {
    if (this.hasUserTarget) {
      this.userSelect = new TomSelect(this.userTarget);
    }

    if (this.hasTimezoneTarget) {
      this.timezoneSelect = new TomSelect(this.timezoneTarget, {
        maxOptions: null,
      });

      this.timezoneSelect.on("change", (timezone) => {
        if (this.hasScheduleTimeZoneTarget) {
          this.scheduleTimeZoneTarget.textContent = timezone;
        }
      });
    }

    if (this.hasMachinesTarget) {
      this.machinesSelect = new TomSelect(this.machinesTarget, {
        plugins: ["remove_button"],
        maxOptions: null,
      });
    }

    if (this.hasPaceMachinesTarget) {
      this.paceMachinesSelect = new TomSelect(this.paceMachinesTarget, {
        plugins: ["remove_button"],
      });
    }

    if (this.hasFactoryTarget) {
      this.factorySelect = new TomSelect(this.factoryTarget, {
        plugins: ["remove_button"],
      });
    }

    if (this.hasIntervalTarget) {
      const selectedInterval = this.intervalTarget.value;
      this.setFrequencyContainerVisibility(selectedInterval);

      this.intervalSelect = new TomSelect(this.intervalTarget);
      this.intervalSelect.on("change", (selectedInterval) => {
        this.setFrequencyContainerVisibility(selectedInterval);
      });
    }

    if (this.hasExportDestinationTypeTarget) {
      this.exportDestinationTypeSelect = new TomSelect(
        this.exportDestinationTypeTarget
      );

      this.exportDestinationTypeSelect.on("change", (selectedType) => {
        this.setExportDestinationTypeVisibility(selectedType);
      });
    }

    if (this.hasExportDestinationTypeTarget) {
      this.transformationPlanTypeSelect = new TomSelect(
        this.transformationPlanTypeTarget
      );
    }

    this.destinationUpdateBehaviorSelect = new TomSelect(
      this.destinationUpdateBehaviorTarget
    );
  }

  setExportDestinationTypeVisibility(selectedType) {
    this.setExportDestinationApiVisibility(selectedType);
    this.setExportDestinationGoogleDriveVisibility(selectedType);
    this.setExportDestinationMicrodoftAzureVisibility(selectedType);
  }

  setExportDestinationApiVisibility(selectedType) {
    if (selectedType === "Export::Destination::Api") {
      this.destinationApiContainerTarget.classList.remove("d-none");
      this.transformationPlanTypeSelect.setValue("Transform::Plan::None");
      this.destinationUpdateBehaviorContainerTarget.classList.add("d-none");
    } else {
      this.destinationApiContainerTarget.classList.add("d-none");
      this.transformationPlanTypeSelect.setValue("");
      this.destinationUpdateBehaviorContainerTarget.classList.remove("d-none");
    }
  }

  setExportDestinationGoogleDriveVisibility(selectedType) {
    if (selectedType === "Export::Destination::GoogleDrive") {
      this.destinationGoogleDriveContainerTarget.classList.remove("d-none");
    } else {
      this.destinationGoogleDriveContainerTarget.classList.add("d-none");
    }
  }

  setExportDestinationMicrodoftAzureVisibility(selectedType) {
    if (selectedType === "Export::Destination::MicrosoftAzure") {
      this.destinationMicrosoftAzureContainerTarget.classList.remove("d-none");
      this.initializeExportDestinationMicrosoftAzureStorageTypeInputType();
    } else {
      this.destinationMicrosoftAzureContainerTarget.classList.add("d-none");
    }
  }

  initializeExportDestinationMicrosoftAzureStorageTypeInputType() {
    this.microsoftAzureStorageTypeSelect = new TomSelect(
      this.microsoftAzureStorageTypeTarget
    );

    this.microsoftAzureStorageTypeSelect.on("change", (selectedType) => {
      this.setExportDestinationMicrosoftAzureStorageContainerVisibility(
        selectedType
      );
      this.setExportDestinationMicrosoftAzureStorageFileShareVisibility(
        selectedType
      );
    });

    this.setExportDestinationMicrosoftAzureStorageContainerVisibility(
      this.microsoftAzureStorageTypeSelect.getValue()
    );
    this.setExportDestinationMicrosoftAzureStorageFileShareVisibility(
      this.microsoftAzureStorageTypeSelect.getValue()
    );
  }

  setExportDestinationMicrosoftAzureStorageContainerVisibility(selectedType) {
    if (selectedType === "container") {
      this.microsoftAzureStorageContainerTarget.classList.remove("d-none");
    } else {
      this.microsoftAzureStorageContainerTarget.classList.add("d-none");
    }
  }

  setExportDestinationMicrosoftAzureStorageFileShareVisibility(selectedType) {
    if (selectedType === "file_share") {
      this.microsoftAzureFileShareContainerTarget.classList.remove("d-none");
    } else {
      this.microsoftAzureFileShareContainerTarget.classList.add("d-none");
    }
  }

  setFrequencyContainerVisibility(selectedInterval) {
    if (selectedInterval === "custom_interval") {
      this.customIntervalContainerTarget.classList.remove("d-none");
      this.frequencyContainerTarget.classList.add("d-none");
    } else {
      this.customIntervalContainerTarget.classList.add("d-none");
      this.frequencyContainerTarget.classList.remove("d-none");
    }
  }
}
